import { create } from 'zustand';
import api from '@/features/api/axios';
import { logger } from '@/features/help/utils/logger';

interface LegalState {
  companyName: string | null;
  disclaimerText: string | null;
  isDefault: boolean;
  isLoading: boolean;
  error: string | null;

  // Methods
  fetchDisclaimer: () => Promise<boolean>;
  updateCompanyName: (companyName: string) => Promise<void>;
  updateDisclaimer: (companyName: string, disclaimerText: string) => Promise<void>;
  resetLegalState: () => void;
}

/**
 * Store for managing legal-related state and operations
 */
export const useLegalStore = create<LegalState>((set, _get) => ({
  companyName: null,
  disclaimerText: null,
  isDefault: true,
  isLoading: false,
  error: null,

  /**
   * Fetch the current disclaimer text and company name from the server
   */
  fetchDisclaimer: async () => {
    try {
      set({ isLoading: true, error: null });

      const response = await api.get('/api/legal/disclaimer');
      const { company_name, disclaimer_text, is_default } = response.data;

      set({
        companyName: company_name,
        disclaimerText: disclaimer_text,
        isDefault: is_default,
        isLoading: false,
      });

      logger.info('Legal', 'Disclaimer fetched successfully', {
        hasCompanyName: !!company_name,
        isDefault: is_default,
      });

      return true;
    } catch (error) {
      logger.error('Legal', 'Failed to fetch disclaimer', { error });

      set({
        error: 'Failed to fetch disclaimer',
        isLoading: false,
      });

      return false;
    }
  },

  /**
   * Update just the company name
   */
  updateCompanyName: async (companyName: string) => {
    try {
      set({ isLoading: true, error: null });

      const response = await api.put('/api/legal/company-name', { company_name: companyName });

      set({
        companyName: response.data.company_name,
        isLoading: false,
      });

      logger.success('Legal', 'Company name updated successfully');
    } catch (error) {
      logger.error('Legal', 'Failed to update company name', { error });

      set({
        error: 'Failed to update company name',
        isLoading: false,
      });

      throw new Error('Failed to update company name');
    }
  },

  /**
   * Update both company name and disclaimer text
   */
  updateDisclaimer: async (companyName: string, disclaimerText: string) => {
    try {
      set({ isLoading: true, error: null });

      const response = await api.put('/api/legal/disclaimer', {
        company_name: companyName,
        disclaimer_text: disclaimerText,
      });

      set({
        companyName: response.data.company_name,
        disclaimerText: response.data.disclaimer_text,
        isDefault: false,
        isLoading: false,
      });

      logger.success('Legal', 'Disclaimer updated successfully');
    } catch (error) {
      logger.error('Legal', 'Failed to update disclaimer', { error });

      set({
        error: 'Failed to update disclaimer',
        isLoading: false,
      });

      throw new Error('Failed to update disclaimer');
    }
  },

  /**
   * Reset the legal state (used for logout)
   */
  resetLegalState: () => {
    set({
      companyName: null,
      disclaimerText: null,
      isDefault: true,
      isLoading: false,
      error: null,
    });
  },
}));
